import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XCircle, CheckCircle2, BookOpen, Music, ClipboardList, CalendarDays, MessageSquare, Palette } from 'lucide-react';
import { usePricing } from '../contexts/PricingContext';
const safePilatesComparisons = [{
  situation: 'Alumna con hernia lumbar',
  without: 'Improvisás o evitás trabajar con ella',
  with: 'Protocolo exacto: qué hacer, qué no hacer, qué decir'
}, {
  situation: 'Alumna con prótesis de cadera',
  without: 'No sabés qué está contraindicado',
  with: 'Lista de restricciones y ejercicios adaptados por condición'
}, {
  situation: 'Alumna embarazada',
  without: 'Miedo a lastimar o quedar mal',
  with: 'Guía por trimestre con todos los ejercicios adaptados'
}, {
  situation: 'Alumna con osteoporosis',
  without: 'Evitás todo por las dudas',
  with: 'Protocolo claro de qué sí y qué no según el grado'
}, {
  situation: 'Situación de urgencia',
  without: 'Pánico y sin saber qué hacer',
  with: 'Protocolo de emergencia paso a paso listo para usar'
}, {
  situation: 'Alumna nueva con condición especial',
  without: 'La ficha de anamnesis no te dice qué hacer',
  with: 'Tabla de equivalencias: si el PDF dice X, para esa condición hacé Y'
}];
const standardIncludes = [{
  icon: BookOpen,
  title: '6 Tomos Completos',
  desc: 'Clásico · Fuerza · Flow · Elementos · Adultos Mayores · Full Power — 60+ clases listas para dar.'
}, {
  icon: Music,
  title: 'Bono: Playlist Curada por Emoción',
  desc: '6 playlists con BPM específico por tomo. La música correcta para cada clase.'
}, {
  icon: ClipboardList,
  title: 'Bono: Ficha de Anamnesis Profesional',
  desc: 'Formulario completo, ficha express, ficha para adultos mayores y consentimiento informado.'
}, {
  icon: CalendarDays,
  title: 'Bono: Calendario Anual de Planificación',
  desc: '12 meses organizados semana a semana. Qué tomo, qué clase y qué meta cada semana.'
}, {
  icon: MessageSquare,
  title: 'Bono: Script de Primera Clase',
  desc: 'Qué decir en los 5 momentos clave para que la alumna nueva se quede y pague el mes.'
}, {
  icon: Palette,
  title: 'Bono: Pack de Flyers para WhatsApp e IG',
  desc: '18 diseños editables en Canva + calendario de 30 días de contenido listo.'
}];
export const SafePilatesComparison = () => {
  const {
    selectedOption
  } = usePricing();
  return <section className="py-10 md:py-14 bg-white" data-id="element-273">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-274">
        <AnimatePresence mode="wait" data-id="element-275">
          {selectedOption === 'premium' ? <motion.div key="premium" initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -20
        }} transition={{
          duration: 0.4
        }} data-id="element-276">
              <div className="text-center mb-8 md:mb-12" data-id="element-277">
                <h2 className="text-2xl md:text-4xl font-bold text-charcoal font-playfair mb-3" data-id="element-278">
                  ¿QUÉ AGREGA EL{' '}
                  <span className="text-sage" data-id="element-279">SAFE PILATES PRO™</span>?
                </h2>
                <p className="text-base md:text-xl text-gray-600" data-id="element-280">
                  El sistema de adaptaciones que hace que ninguna alumna sea
                  "demasiado difícil"
                </p>
              </div>

              {/* Mobile: Card layout */}
              <div className="md:hidden space-y-3" data-id="element-281">
                {safePilatesComparisons.map((row, index) => <motion.div key={index} initial={{
              opacity: 0,
              y: 10
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: index * 0.05
            }} className="bg-offwhite rounded-xl p-4 border border-gray-100" data-id="element-282">
                    <p className="font-bold text-charcoal text-sm mb-2" data-id="element-283">
                      {row.situation}
                    </p>
                    <div className="flex items-start gap-2 mb-2" data-id="element-284">
                      <XCircle className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" data-id="element-285" />
                      <span className="text-xs text-gray-500" data-id="element-286">
                        {row.without}
                      </span>
                    </div>
                    <div className="flex items-start gap-2" data-id="element-287">
                      <CheckCircle2 className="w-4 h-4 text-sage flex-shrink-0 mt-0.5" data-id="element-288" />
                      <span className="text-xs text-gray-800 font-medium" data-id="element-289">
                        {row.with}
                      </span>
                    </div>
                  </motion.div>)}
              </div>

              {/* Desktop: Table layout */}
              <div className="hidden md:block overflow-x-auto rounded-2xl shadow-sm border border-gray-200" data-id="element-290">
                <table className="w-full text-left border-collapse" data-id="element-291">
                  <thead data-id="element-292">
                    <tr data-id="element-293">
                      <th className="p-6 bg-gray-50 font-bold text-charcoal border-b border-gray-200 w-1/3" data-id="element-294">
                        Situación
                      </th>
                      <th className="p-6 bg-red-50 font-bold text-red-800 border-b border-red-100 w-1/3" data-id="element-295">
                        Sin Safe Pilates Pro
                      </th>
                      <th className="p-6 bg-sage-50 font-bold text-sage-800 border-b border-sage-100 w-1/3" data-id="element-296">
                        Con Safe Pilates Pro
                      </th>
                    </tr>
                  </thead>
                  <tbody data-id="element-297">
                    {safePilatesComparisons.map((row, index) => <motion.tr key={index} initial={{
                  opacity: 0
                }} whileInView={{
                  opacity: 1
                }} viewport={{
                  once: true
                }} transition={{
                  delay: index * 0.1
                }} className="border-b border-gray-100 last:border-0" data-id="element-298">
                        <td className="p-6 font-medium text-gray-800" data-id="element-299">
                          {row.situation}
                        </td>
                        <td className="p-6 text-gray-600 bg-red-50/30" data-id="element-300">
                          <div className="flex items-start gap-2" data-id="element-301">
                            <XCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" data-id="element-302" />
                            <span data-id="element-303">{row.without}</span>
                          </div>
                        </td>
                        <td className="p-6 text-gray-800 bg-sage-50/30 font-medium" data-id="element-304">
                          <div className="flex items-start gap-2" data-id="element-305">
                            <CheckCircle2 className="w-5 h-5 text-sage flex-shrink-0 mt-0.5" data-id="element-306" />
                            <span data-id="element-307">{row.with}</span>
                          </div>
                        </td>
                      </motion.tr>)}
                  </tbody>
                </table>
              </div>
            </motion.div> : <motion.div key="standard" initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -20
        }} transition={{
          duration: 0.4
        }} data-id="element-308">
              <div className="text-center mb-8 md:mb-12" data-id="element-309">
                <h2 className="text-2xl md:text-4xl font-bold text-charcoal font-playfair mb-3" data-id="element-310">
                  ¿QUÉ INCLUYE TU <span className="text-sage" data-id="element-311">PILATES 360</span>
                  ?
                </h2>
                <p className="text-base md:text-xl text-gray-600" data-id="element-312">
                  Todo lo que necesitás para organizar tu año completo de clases
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4" data-id="element-313">
                {standardIncludes.map((item, index) => <motion.div key={index} initial={{
              opacity: 0,
              y: 10
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: index * 0.05
            }} className="bg-offwhite rounded-xl p-4 md:p-5 border border-gray-100 flex items-start gap-3" data-id="element-314">
                    <div className="w-9 h-9 md:w-10 md:h-10 bg-sage-100 rounded-lg flex items-center justify-center flex-shrink-0" data-id="element-315">
                      <item.icon className="w-4 h-4 md:w-5 md:h-5 text-sage-700" data-id="element-316" />
                    </div>
                    <div data-id="element-317">
                      <h3 className="text-sm md:text-base font-bold text-charcoal mb-1" data-id="element-318">
                        {item.title}
                      </h3>
                      <p className="text-xs md:text-sm text-gray-600 leading-relaxed" data-id="element-319">
                        {item.desc}
                      </p>
                    </div>
                  </motion.div>)}
              </div>
            </motion.div>}
        </AnimatePresence>
      </div>
    </section>;
};